import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Study } from '@models';
import {
  AppHeaderService,
  PortfolioLoadingService,
  PortfolioStatsService,
  StudyFilterService,
  StudyService
} from '@services';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ConfirmationResult } from '../modal-stage/modal-stage.component';

@Component({
  selector: 'summary-card',
  templateUrl: './summary-card.component.html',
  styleUrl: './summary-card.component.scss'
})
export class SummaryCardComponent implements OnInit, OnDestroy {
  isLoading = true;
  startupStudiesCount = 0;
  enrollmentStudiesCount = 0;
  maintenanceStudiesCount = 0;
  closingStudiesCount = 0;
  sponsorSelected = 'All';
  sponsorsCount = 0;
  onlySponsorName = '';
  tempCount = 0;

  // Modal properties
  showModal = false;
  modalTitle = '';
  selectedStage = '';
  filteredTableData = new MatTableDataSource<Study>([]);
  hasFilteredData = true;
  private previousFilters: any = null;

  private subscriptions = new Subscription();

  private appHeaderService = inject(AppHeaderService);
  private portfolioStatsService = inject(PortfolioStatsService);
  private portfolioLoadingService = inject(PortfolioLoadingService);
  private studyFilterService = inject(StudyFilterService);
  private studyService = inject(StudyService);

  ngOnInit(): void {
    const header = this.appHeaderService.getCurrentHeaderState();
    this.sponsorSelected = header.selectedSponsor;
    this.onlySponsorName = (header.sponsors ?? [])[0] ?? '';
    this.sponsorsCount = (header.sponsors ?? []).length;

    this.subscriptions.add(
      this.portfolioLoadingService.isLoading$.subscribe((isLoading) => {
        this.isLoading = isLoading;
      })
    );

    this.subscriptions.add(
      this.portfolioStatsService.startupStudiesCount$.subscribe((count) => {
        this.startupStudiesCount = count;
      })
    );

    this.subscriptions.add(
      this.portfolioStatsService.enrollmentStudiesCount$.subscribe((count) => {
        this.enrollmentStudiesCount = count;
      })
    );

    this.subscriptions.add(
      this.portfolioStatsService.maintenanceStudiesCount$.subscribe((count) => {
        this.maintenanceStudiesCount = count;
      })
    );

    this.subscriptions.add(
      this.portfolioStatsService.closingStudiesCount$.subscribe((count) => {
        this.closingStudiesCount = count;
      })
    );

    this.subscriptions.add(
      this.studyService
        .getCachedStudiesPortfolio()
        .pipe(filter((studies: Study[] | null): studies is Study[] => studies !== null))
        .subscribe((studies: Study[]) => {
          this.processAndInitializeFilters(studies);
        })
    );

    this.subscriptions.add(
      this.studyFilterService.filterState$.subscribe((filterState) => {
        this.filteredTableData.data = filterState.filteredData;
        this.hasFilteredData = filterState.filteredData.length > 0;
      })
    );

    this.subscriptions.add(
      this.appHeaderService.getSponsorApplied().subscribe((sponsor) => {
        this.sponsorSelected = sponsor;
      })
    );
  }

  private processAndInitializeFilters(studies: Study[]): void {
    const processedData = studies.map((study) => {
      const processedStudy = { ...study };

      if (study.country != null) {
        const countriesList = study.country.split(',').map((country) => country.trim());
        processedStudy.countryList = countriesList;
        processedStudy.country = countriesList.length > 1 ? 'Multiple' : countriesList[0];
      }

      // Process ppdRegion first, falling back to region if ppdRegion is not available
      if (study.ppdRegion == null && study.region != null) {
        processedStudy.ppdRegion = study.region;
      }

      if (processedStudy.ppdRegion != null) {
        const ppdRegionsList = processedStudy.ppdRegion.split(',').map((region) => region.trim());
        processedStudy.ppdRegionList = ppdRegionsList;
        processedStudy.ppdRegion = ppdRegionsList.length > 1 ? 'Multiple' : ppdRegionsList[0];
      }

      if (study.region != null) {
        const regionsList = study.region.split(',').map((region) => region.trim());
        processedStudy.regionList = regionsList;
        processedStudy.region = regionsList.length > 1 ? 'Multiple' : regionsList[0];
      }

      return processedStudy;
    });

    this.studyFilterService.initializeFilters(processedData);

    // Apply or remove client filter based on sponsor selection
    if (this.sponsorSelected && this.sponsorSelected !== 'All') {
      this.studyFilterService.addClientFilter(true, this.sponsorSelected);
    } else {
      // Ensure client filter is removed when 'All' is selected
      this.studyFilterService.removeClientFilter();
    }
  }

  onStageCardClick(stage: string, count: number): void {
    this.tempCount = count;
    if (this.isLoading) {
      return;
    }

    const currentState = this.studyFilterService.getCurrentState();
    this.previousFilters = JSON.parse(
      JSON.stringify({
        appliedFilters: Object.fromEntries(
          Object.entries(currentState.appliedFilters).map(([key, set]) => [key, Array.from(set)])
        )
      })
    );

    this.studyFilterService.clearAllFilters('table');
    this.studyFilterService.addFilter('studyStage', stage, 'table');

    this.selectedStage = stage;
    this.modalTitle = `${count} studies on ${stage.toLowerCase()}`;
    this.showModal = true;
  }

  onModalClosed(result: ConfirmationResult): void {
    this.showModal = false;

    if (result === 'yes') {
      if (this.tempCount === 0) {
        this.studyFilterService.clearAllFilters('table');
      }
      this.previousFilters = null;
      this.selectedStage = '';
    } else {
      if (this.previousFilters) {
        this.studyFilterService.clearAllFilters('table');

        const filtersToRestore = this.previousFilters.appliedFilters;
        Object.entries(filtersToRestore).forEach(([field, values]) => {
          (values as string[]).forEach((value) => {
            this.studyFilterService.addFilter(field, value, 'table');
          });
        });

        this.previousFilters = null;
      }
      this.selectedStage = '';
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
